// Inline hint banner that appears in OrgLocationsForm when the user
// picks location_type ∈ {virtual, corporate}. Explains why the row
// still needs a physical anchor address (workflow doc §11) and
// surfaces the one-click "Use corporate HQ address" prefill button
// when an active corporate-typed location with a street address
// exists for the org. When no such anchor exists yet, shows a hint
// nudging the user to create one first.
//
// Extracted from OrgLocationsForm in slice 13a to keep the parent
// form under the 300-line soft cap.
import type { OrgLocation } from "@/lib/orgLocations/orgLocationsTypes";

type Props = {
  type: "virtual" | "corporate";
  anchor: OrgLocation | null;
  onPrefill: () => void;
  busy: boolean;
};

export function OrgLocationsFormOnlineHint({ type, anchor, onPrefill, busy }: Props) {
  return (
    <div className="rounded-lg border border-fuchsia-200 bg-fuchsia-50/60 px-3 py-2 text-xs text-fuchsia-900 dark:border-fuchsia-900/50 dark:bg-fuchsia-950/30 dark:text-fuchsia-200">
      <p className="font-medium">
        {type === "virtual"
          ? "Online-only / virtual sales location"
          : "Corporate / back-office location"}
      </p>
      <p className="mt-0.5 text-fuchsia-900/80 dark:text-fuchsia-200/80">
        {type === "virtual"
          ? "Customers buy through your online channel, but orders still need a physical anchor for shipping & routing. Use your corporate HQ address below."
          : "Back-office / admin location — usually hidden from customer-facing maps and used as an anchor for online orders."}
      </p>
      {anchor ? (
        <button
          type="button"
          className="mt-2 inline-flex items-center gap-1 rounded-md border border-fuchsia-300 bg-white px-2 py-1 text-xs font-medium text-fuchsia-800 hover:bg-fuchsia-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-fuchsia-800/60 dark:bg-zinc-900 dark:text-fuchsia-200 dark:hover:bg-fuchsia-950/40"
          onClick={onPrefill}
          disabled={busy}
          title={`Copy address from "${anchor.name}"`}
        >
          ↳ Use corporate HQ address ({anchor.name})
        </button>
      ) : (
        <p className="mt-1 text-[11px] italic text-fuchsia-900/70 dark:text-fuchsia-200/70">
          No corporate-type location with a street address exists yet. Add one
          first to enable one-click HQ prefill.
        </p>
      )}
    </div>
  );
}
